import { useQuery } from '@tanstack/react-query';
import type { CancellationReason, PlanSet } from '@tabbio/types';
import {
  NO_RETENTION_OPTIONS,
  fetchRetentionOptions,
} from '../lib/subscription-journey/retention-options';
import { useAuth } from './useAuth';

export const RETENTION_OPTIONS_QUERY_KEY = [
  'subscription',
  'retention-options',
] as const;

/**
 * Pause capability + (dormant) retention offer for the cancel journey. While
 * loading or on error, `options` is the safe fallback (no pause, no offer), so
 * a failed request can never block cancellation.
 */
export function useRetentionOptions(input: {
  planSet: PlanSet;
  companyId?: string | null;
  reason?: CancellationReason | null;
  enabled?: boolean;
}) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: [
      ...RETENTION_OPTIONS_QUERY_KEY,
      user?.id ?? null,
      input.planSet,
      input.companyId ?? null,
      input.reason ?? null,
    ],
    queryFn: () =>
      fetchRetentionOptions({
        planSet: input.planSet,
        companyId: input.companyId ?? null,
        reason: input.reason ?? null,
      }),
    enabled: (input.enabled ?? true) && Boolean(user?.id),
    staleTime: 60_000,
  });
  return {
    options: query.data ?? NO_RETENTION_OPTIONS,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
